import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import ReportBody from './ReportBody'
import ReportToolbar from './ReportToolbar'
import InvestigateTab from './InvestigateTab'
import Loader from '../Material/Loader'
import useActivityReport from './useActivityReport'
import { ROLE_OPTIONS, filterRows, previousRange } from './activityReport'
import { getReport, hasBranchAttributionFor, lineOfBusinessTabFor, scopeLabelFor, scopeTabFor } from './reportRegistry'
import './Reports.css'

// Last 14 days, ending today (inclusive on both ends, so "14 days" means 14
// calendar days shown, not 14 days back from yesterday).
const defaultRange = () => ({
    startDate: moment().subtract(13, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
})

// The panel's starting filter state -- every dimension empty except roles,
// which starts with Reps already selected (`ROLE_OPTIONS.filter(...)`
// below). That token is an ordinary, removable toolbar token from the first
// render on, exactly like the old exclude-users default it replaces: nothing
// re-adds it once a caller's own `filters` state no longer has it. A static
// value here, not something an effect seeds in once rows arrive -- an effect
// would race the first fetch (either it runs before rows exist and has
// nothing to seed, or it re-runs whenever rows change and silently re-adds a
// token the user just removed). `excludeUsers` starts empty: it is purely
// for hiding specific people now, with no default population of its own (see
// `roleFor`/`ROLE_OPTIONS` in activityReport.js).
const DEFAULT_FILTERS = {
    users: [],
    branches: [],
    scope: [],
    groups: [],
    actions: [],
    roles: ROLE_OPTIONS.filter((option) => option.value === 'rep'),
    excludeUsers: [],
}

/**
 * The embeddable unit a listing page drops in to get that module's activity
 * log as a toggle, in place of the old standalone `/reports` page. One
 * `reportId` per mounted panel: a listing page only ever shows its own
 * report, so nothing here branches on which report it is -- every report is
 * driven off `reportRegistry.js`.
 *
 * Two tabs, fixed regardless of report: Activity (the toolbar plus
 * `ReportBody`) and Investigate. What used to be separate "By X" tabs
 * (module, the optional scope tab, Users) are now one Group-by control
 * inside `ReportToolbar`, next to the Day/Week/Month switch -- see
 * `grouping`/`groupingOptions` below. That is a display concern only -- the
 * date range and every filter token scope every grouping identically, so
 * changing the grouping never resets them. Investigate is a one-actor
 * drill-down over the report's raw history rows (`InvestigateTab.js`) rather
 * than another `ReportBody` layout -- it renders in place of `ReportBody`
 * when active, and unlike Activity it reads the *unfiltered* rows
 * (`useActivityReport`'s `rows`, not `visibleRows`), since its own actor
 * picker is a lookup, not something the toolbar's filters should narrow.
 * On the Investigate tab the heading and subheading stay, but `ReportToolbar`
 * is hidden: `InvestigateTab` carries its own filter row (date control, one
 * actor, actions), so the toolbar's row filters would not apply to what it
 * shows.
 * The one thing that does reset the filters, granularity, and grouping (the
 * date range is kept) is the report itself changing under a mounted panel: options are
 * per-report, so a filter selected on one report could silently narrow
 * everything away on another. `ReportToolbar` remounts (`key={reportId}`)
 * for the same reason -- its revealed-token state is local and independent
 * of the filter values themselves.
 *
 * The panel's own heading and subheading sit above the tabs on every tab, labelling this
 * panel -- a listing page's own page-level heading, if it has one, lives
 * outside this component entirely. The heading pairs the report's name with
 * "activity log" (not "report"), a history icon, and a subtitle that says
 * outright these are counts of recorded create/edit events read back from the
 * history log, not live totals off the record itself -- everything on this
 * panel is one BigQuery query over `bos_models_history_v2`, and the framing
 * exists so a viewer never mistakes it for a query over live business data.
 * The tabs are plain `role="tab"` buttons rather than
 * the shared `ListingTabs` component: this switch is subordinate to a
 * listing page's own primary navigation (see Reports.css), and reusing
 * ListingTabs here would make it read as a second, competing rail of the
 * same kind of control.
 */
// The two fixed tabs -- unlike the old per-report tab list, these never vary
// with `report`, so they live outside the component rather than being
// rebuilt (or memoized) on every render.
const TABS = [
    { id: 'activity', name: 'Activity' },
    { id: 'investigate', name: 'Investigate' },
]

const ReportsPanel = ({ reportId }) => {
    const [range, setRange] = useState(defaultRange)
    const [view, setView] = useState('chart')
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [activeTab, setActiveTab] = useState('activity')
    // The Day/Week/Month time-bucket switch -- see ReportToolbar's segmented
    // control and ReportBody's use of it. Panel-level, like `filters` and
    // `range`, because it re-buckets every chart under every grouping (the
    // module's own subject, the scope grouping, Users) at once; Investigate
    // has no time axis to bucket, so it never receives this.
    const [granularity, setGranularity] = useState('day')
    // Which of `groupingOptions` (below) `ReportBody` renders on the Activity
    // tab -- the old module/scope/Users tabs collapsed into this one
    // Group-by control in `ReportToolbar`. Panel-level and persisted across a
    // switch to Investigate and back, exactly like `granularity`: it is a
    // display concern only, so nothing about switching tabs should reset it.
    const [grouping, setGrouping] = useState('module')
    // Set by a heatmap cell's "View in Investigate" link (see
    // `handleInvestigate` below) and handed to `InvestigateTab` as
    // `initialActorName`/`initialActorId` -- cleared again once that tab has
    // actually mounted with them (the "mounted" effect further down), or as
    // soon as Investigate is left without ever mounting with them (the
    // "leaving" effect right after it), so a later *manual* switch to
    // Investigate never re-applies a stale preselection. `pendingActorId` is
    // the row's `userId` when the heatmap could tell -- `undefined` when it
    // can't (see `buildHeatmap`'s own `userId` field in activityReport.js) --
    // and lets `InvestigateTab` match by id instead of by (possibly
    // ambiguous) display name.
    const [pendingActorName, setPendingActorName] = useState(null)
    const [pendingActorId, setPendingActorId] = useState(undefined)

    const report = getReport(reportId)

    useEffect(() => {
        setFilters(DEFAULT_FILTERS)
        setGranularity('day')
        setGrouping('module')
        setPendingActorName(null)
        setPendingActorId(undefined)
    }, [reportId])

    // The role/user/branch/group/exclude-user filters go to the hook; the
    // action filter is applied here, because it is a display concern and the
    // hook derives its dropdown options from the unfiltered rows.
    //
    // A group expands to its members' ids, so several groups union together and
    // then intersect with whatever else is selected. `excludeUserIds` is its
    // own dimension rather than folded into `userIds` -- see `filterRows`'s
    // docstring for why an intersecting exclude pass is what makes exclusion
    // win over inclusion for the same person.
    //
    // The exclude-users token holds id-matched entries only -- the No Actor
    // sentinel (SYSTEM_EXCLUDE_VALUE) and any person picked from the
    // dropdown; see `excludeUserOptions` in useActivityReport.js. A service
    // account such as `cloud_functions` is excludable by id there too, or
    // more broadly by the Role filter, so there is nothing left for this
    // token to match by username.
    const rowFilters = useMemo(
        () => ({
            roles: filters.roles.map((item) => item.value),
            users: filters.users.map((item) => item.value),
            branches: filters.branches.map((item) => item.value),
            scope: filters.scope.map((item) => item.value),
            userIds: [...new Set(filters.groups.flatMap((group) => group.userIds || []))],
            excludeUserIds: filters.excludeUsers.map((item) => item.value),
        }),
        [filters]
    )

    // The window the stat tiles compare against (see `previousRange`). Its
    // dates depend only on `range`; `granularity` only changes how the window
    // is named on the tiles.
    const comparison = useMemo(
        () => previousRange(range.startDate, range.endDate, granularity),
        [range.startDate, range.endDate, granularity]
    )

    const {
        loading,
        error,
        rows,
        filteredRows,
        previousFilteredRows,
        userOptions,
        branchOptions,
        scopeOptions,
        groupOptions,
        excludeUserOptions,
        canViewAllUsers,
        catalogLoading,
        currentUserId,
    } = useActivityReport({
        reportId,
        startDate: range.startDate,
        endDate: range.endDate,
        previousStartDate: comparison.startDate,
        previousEndDate: comparison.endDate,
        filters: rowFilters,
    })

    const actionValues = useMemo(() => filters.actions.map((item) => item.value), [filters.actions])
    const visibleRows = useMemo(
        () => filterRows(filteredRows, { actions: actionValues }),
        [filteredRows, actionValues]
    )
    // Same action filter over the previous window, so both sides of each
    // tile's comparison are narrowed identically.
    const visiblePreviousRows = useMemo(
        () => (previousFilteredRows ? filterRows(previousFilteredRows, { actions: actionValues }) : null),
        [previousFilteredRows, actionValues]
    )

    const options = useMemo(
        () => ({
            roles: ROLE_OPTIONS,
            users: userOptions,
            branches: branchOptions,
            scope: scopeOptions,
            groups: groupOptions,
            actions: report.actions.map((action) => ({ value: action.value, label: action.label })),
            excludeUsers: excludeUserOptions,
        }),
        [userOptions, branchOptions, scopeOptions, groupOptions, report, excludeUserOptions]
    )

    // Without the permission you are the only person in the data, so filtering by
    // user, group, or excluded user can only ever narrow to yourself or to
    // nothing -- exclude-users is as meaningless there as the include filter.
    // Branch and the report's own scope dimension (see below) stay available
    // either way: neither one identifies a person, so neither is meaningless
    // for a self-scoped viewer the way the people filters are. `roles` is
    // offered to both: it is a harmless no-op for a self-scoped viewer, whose
    // rows are all their own rep rows anyway (a mobile driver or a service
    // account never has permission to view this panel), but keeping one rule
    // rather than a permission-specific exception is simpler to reason about,
    // and it costs a self-scoped viewer nothing -- narrowing to Reps only, or
    // clearing the token, changes nothing they see.
    const permissionScopedKinds = canViewAllUsers
        ? ['roles', 'branches', 'scope', 'users', 'groups', 'actions', 'excludeUsers']
        : ['roles', 'branches', 'scope', 'actions']
    // Two independent reasons a kind can drop out of that list regardless of
    // permission, mirroring the two independent `ReportSpec` fields they read
    // (`has_branch_attribution` / `scope_label` in activity/registry.py):
    //
    // - A report with no branch attribution (currently only `boards` --
    //   task_rabbit.Board has no branch/tenant/org link at all, see
    //   `hasBranchAttributionFor`) has nothing for a Branch filter to narrow:
    //   every row's branch_name is the same `NOT_BRANCH_ATTRIBUTED`
    //   placeholder.
    // - A report with no declared `scopeLabel` (every report except `boards`
    //   and `inbox`, see `scopeLabelFor`) has no second dimension at all --
    //   every row's `scopeNames` is `[]` -- so there is nothing for that
    //   filter to offer either.
    //
    // Both checks are driven off the report's own registry entry, never off
    // `report.id`, so a future report declaring either flag gets the right
    // toolbar with no change here.
    const kindAvailableForReport = {
        branches: hasBranchAttributionFor(report),
        scope: Boolean(scopeLabelFor(report)),
    }
    const availableKinds = permissionScopedKinds.filter((kind) => kindAvailableForReport[kind] !== false)

    // `catalogLoading` covers the REPORT_CATALOG query specifically, which is
    // where `canViewAllUsers` comes from. Folding it into the loading state
    // means the layout choice is only ever made once the catalog has actually
    // answered -- never guessed at its `false` default and then flipped once
    // the real value arrives.
    const isLoading = loading || catalogLoading

    const scopeTab = useMemo(() => scopeTabFor(report), [report])
    const lineOfBusinessTab = useMemo(() => lineOfBusinessTabFor(report), [report])

    // The options behind the Group-by control in ReportToolbar -- what used
    // to be the module/scope/Users tabs, in the same order those tabs used to
    // appear in. "By X" rather than bare "Incidents" / "Users": these are two
    // lenses on the same activity, not two different subjects, and the old
    // labels read as separate things. Deliberately not "Report by …" -- the
    // whole point of the surrounding copy is that this is an activity log,
    // not a report.
    const groupingOptions = useMemo(
        () => [
            { id: 'module', name: `By ${report.noun}` },
            // The optional third grouping a report can declare (see the doc
            // comment above) -- absent for every report but boards today.
            ...(scopeTab ? [{ id: scopeTab.id, name: scopeTab.name }] : []),
            // Every report with branch attribution -- see `lineOfBusinessTabFor`.
            ...(lineOfBusinessTab ? [{ id: lineOfBusinessTab.id, name: lineOfBusinessTab.name }] : []),
            { id: 'users', name: 'By Users' },
        ],
        [report, scopeTab, lineOfBusinessTab]
    )

    // `grouping` is reset to `'module'` by the `reportId` effect above, but
    // that effect only runs after the render in which the new report first
    // appears. For that one render `grouping` can still name an option the new
    // report never declares (currently only `scope`, "By Board"). No button
    // would then show as pressed, and `ReportBody` would be handed a layout the
    // report cannot render. The clamp covers that render, so every read site
    // (the toolbar's pressed state and the `layout` prop) always sees a grouping
    // the current report offers.
    const effectiveGrouping = groupingOptions.some((option) => option.id === grouping) ? grouping : 'module'
    const isInvestigate = activeTab === 'investigate'

    // The same range setter for both ReportToolbar's date button and the
    // Investigate query bar's own DateRangeControl (`InvestigateTab.js`) --
    // whichever one is mounted, moving the range always goes through this
    // one function, so the two never drift into different date-parsing
    // behaviour.
    const handleDateChange = (selection) =>
        setRange({
            startDate: moment(selection.startDate).format('YYYY-MM-DD'),
            endDate: moment(selection.endDate).format('YYYY-MM-DD'),
        })

    // A heatmap cell's "View in Investigate" link (`ActivityHeatmap` ->
    // `ReportBody` -> here): jump to the Investigate tab already scoped to
    // exactly what that cell counted. The date range narrows to the cell's
    // own period *clipped* to whatever range is already selected -- Investigate
    // should never claim to cover days the heatmap itself never counted.
    // Plain string comparison is enough: every bound here is already a
    // 'YYYY-MM-DD' string, which sorts lexicographically exactly like a
    // date. Belt-and-braces rather than strictly load-bearing: `periodsBetween`
    // (activityReport.js) builds every bucket's `start`/`end` out of
    // `daysBetween(startDate, endDate)`, so a period can never actually run
    // past the range it was built from in the first place -- this clip is
    // defensive against that invariant ever changing (or a future caller
    // constructing a period some other way), not a case that fires today.
    const handleInvestigate = (rowName, period, userId) => {
        if (!period) return
        setRange({
            startDate: period.start > range.startDate ? period.start : range.startDate,
            endDate: period.end < range.endDate ? period.end : range.endDate,
        })
        setPendingActorName(rowName)
        setPendingActorId(userId)
        setActiveTab('investigate')
    }

    // Clears `pendingActorName`/`pendingActorId` once `InvestigateTab` has
    // actually mounted with them as `initialActorName`/`initialActorId` --
    // not in `handleInvestigate` itself, which would clear them in the same
    // batch it sets them, before the tab ever sees them (see
    // `InvestigateTab`'s own props, read only via a mount-time `useState`
    // initializer). Gated on the exact same condition that mounts
    // `InvestigateTab` below (`!isLoading && !error && isInvestigate`), not
    // on `isInvestigate` alone: `handleInvestigate` narrows `range`, which
    // re-fires the report query and flips `isLoading` back to true for a
    // render or two, during which `InvestigateTab` is replaced by the
    // "Loading report..." message -- clearing on `isInvestigate` alone would
    // fire during that window, before `InvestigateTab` ever mounts with the
    // value, and the preselection would be lost before it was ever used.
    const isInvestigateMounted = !isLoading && !error && isInvestigate
    useEffect(() => {
        if (isInvestigateMounted && pendingActorName) {
            setPendingActorName(null)
            setPendingActorId(undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInvestigateMounted])

    // The other side of the same cleanup: if Investigate is left again --
    // the report errors before it ever mounts, or the user clicks back to
    // Activity while it's still loading -- without the effect above ever
    // having run, `pendingActorName`/`pendingActorId` would otherwise sit
    // there until the *next* manual switch to Investigate, which would then
    // wrongly preselect and auto-fetch an actor nobody just asked for. Safe
    // to run alongside the effect above (both can fire in the same render,
    // in either order): `handleInvestigate` always sets `isInvestigate` to
    // `true` in the same batch it sets the pending actor, so this effect's
    // own `!isInvestigate` condition is never true on the render that
    // introduces a new pending actor -- only on a later one, after it either
    // got consumed or the tab was left.
    useEffect(() => {
        if (!isInvestigate) {
            setPendingActorName(null)
            setPendingActorId(undefined)
        }
    }, [isInvestigate])

    return (
        <div>
                        <h3 className="reports-panel-title">
                <i className="fa fa-history reports-panel-title-icon" aria-hidden="true"></i>
                {report.name} activity log
            </h3>
            <p className="reports-panel-subtitle">{report.subtitle}</p>

            <div className="reports-panel-tablist" role="tablist" aria-label={`${report.name} report views`}>
                {TABS.map((tab) => {
                    const isActive = activeTab === tab.id
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            id={`reports-panel-tab-${reportId}-${tab.id}`}
                            aria-selected={isActive}
                            aria-controls={`reports-panel-tabpanel-${reportId}-${tab.id}`}
                            tabIndex={isActive ? 0 : -1}
                            className={`reports-panel-tab${isActive ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.name}
                        </button>
                    )
                })}
            </div>

            <div
                className="reports-card"
                role="tabpanel"
                id={`reports-panel-tabpanel-${reportId}-${activeTab}`}
                aria-labelledby={`reports-panel-tab-${reportId}-${activeTab}`}
            >
                {!isInvestigate && (
                    <ReportToolbar
                        // Remounts on report change: ReportToolbar tracks which filter
                        // tokens are revealed in its own local state, independent of
                        // `filters` itself, so clearing the filter *values* on switch
                        // is not enough to also hide a token that had been opened empty.
                        // A fresh key forces that local state to reset along with it.
                        key={reportId}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        onDateChange={handleDateChange}
                        filters={filters}
                        onFiltersChange={setFilters}
                        options={options}
                        availableKinds={availableKinds}
                        scopeLabel={scopeLabelFor(report)}
                        groupingOptions={groupingOptions}
                        grouping={effectiveGrouping}
                        onGroupingChange={setGrouping}
                        view={view}
                        onViewChange={setView}
                        granularity={granularity}
                        onGranularityChange={setGranularity}
                    />
                )}

                {isLoading && (
                    <div className="reports-status reports-status--loading" role="status">
                        <Loader />
                        <span className="viz-visually-hidden">Loading report…</span>
                    </div>
                )}
                {!isLoading && error && (
                    <p className="reports-status reports-status--error">
                        Unable to load report. Please try again.
                    </p>
                )}
                {!isLoading && !error && isInvestigate && (
                    <InvestigateTab
                        // Remounts on report change, same reason as ReportToolbar's
                        // key above: without it, InvestigateTab's own actor/actions
                        // selection and fetched results would survive a report
                        // change under a mounted panel.
                        key={reportId}
                        report={report}
                        rows={rows}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        onDateChange={handleDateChange}
                        canViewAllUsers={canViewAllUsers}
                        currentUserId={currentUserId}
                        initialActorName={pendingActorName}
                        initialActorId={pendingActorId}
                    />
                )}
                {!isLoading && !error && !isInvestigate && (
                    <ReportBody
                        report={report}
                        rows={visibleRows}
                        view={view}
                        layout={effectiveGrouping}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        onInvestigate={handleInvestigate}
                        canViewAllUsers={canViewAllUsers}
                        granularity={granularity}
                        previousRows={visiblePreviousRows}
                        previousRange={comparison}
                    />
                )}
            </div>
        </div>
    )
}

export default ReportsPanel
